/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { View, Text, FlatList, TextStyle } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import HeaderView from '@airasia-phoenix/react-native/widgets/HeaderView';
import {
  getDeepCopy,
  pd,
  getTextStyle,
  getColor,
} from '@airasia-phoenix/react-native/utils/utils';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import CurrencyRow from './CurrencyRow';

export interface CurrencyItem {
  id: string;
  primaryText: string;
}

export interface CurrenciesViewProps {
  onClose?: () => void;
  onSelect?: (item: CurrencyItem) => void;
  currencyList: CurrencyItem[];
  selectedValue?: CurrencyItem;
  currencyTitle?: string;
  /** Search placeholder text. */
  searchLabel?: string;
  testID?: string;
  /** When true, moves the selected currency to the top of the list. */
  sortSelectedToTop?: boolean;
  /** Show the search bar above the list. */
  showSearch?: boolean;
  /** Bottom padding for the list content. */
  contentPaddingBottom?: number;
  /** Horizontal padding for the list content. */
  contentPaddingHorizontal?: number;
}

const CurrenciesView = ({
  onClose,
  currencyList,
  onSelect,
  selectedValue,
  currencyTitle,
  searchLabel = 'Search',
  sortSelectedToTop = true,
  showSearch = true,
  contentPaddingBottom = 50,
  contentPaddingHorizontal = 0,
}: CurrenciesViewProps) => {
  const { theme } = useUnistyles();

  const [searchValue, setSearchValue] = useState('');
  const [data, setData] = useState<CurrencyItem[]>([]);
  const [selected, setSelected] = useState(selectedValue?.id ?? 'MYR');

  const flatListRef = useRef<FlatList>(null);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const originalList = useMemo(
    () => getDeepCopy(currencyList) as CurrencyItem[],
    [currencyList],
  );

  const greenCheckIcon = useMemo(
    () => ({
      height: 24,
      width: 24,
      icon: pd.icon.main.navigation.check,
      tint: pd.ref.palette.green70,
    }),
    [],
  );

  const selectedTextStyle = useMemo(
    () =>
      getTextStyle(
        pd.sys.typescale.body_text_medium,
        pd.sys.colour.content_primary,
      ) as TextStyle,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `theme` re-triggers pd.sys.* colour resolution, which eslint can't see
    [theme],
  );

  const baseTextStyle = useMemo(
    () =>
      getTextStyle(
        pd.sys.typescale.body_text,
        pd.sys.colour.content_primary,
      ) as TextStyle,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `theme` re-triggers pd.sys.* colour resolution, which eslint can't see
    [theme],
  );

  const sortSelectedFirst = (list: CurrencyItem[], selectedId: string) => {
    const selectedItem = list.find((i) => i.id === selectedId);
    if (selectedItem) {
      return [selectedItem, ...list.filter((i) => i.id !== selectedId)];
    }
    return list;
  };

  useEffect(() => {
    const lower = searchValue.trim().toLowerCase();
    let filtered = originalList;

    if (lower) {
      filtered = originalList.filter((item) =>
        item.primaryText.toLowerCase().includes(lower),
      );
    }

    if (sortSelectedToTop && selected) {
      filtered = sortSelectedFirst(filtered, selected);
    }

    setData(filtered);
  }, [originalList, selected, searchValue, sortSelectedToTop]);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, []);

  const handleClose = () => onClose?.();

  const handleSearch = (text: string) => {
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setSearchValue(text);
    }, 200);
  };

  const handleSelect = (item: CurrencyItem) => {
    setSelected(item.id);
    onSelect?.(item);
    if (sortSelectedToTop) {
      flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
    }
  };

  const handleClearSearch = () => setSearchValue('');

  const renderList = useMemo(() => {
    return data.map((item) => ({
      ...item,
      endElement:
        item.id === selected ? { iconViewProps: greenCheckIcon } : undefined,
      primaryTextStyle: {
        ...(item.id === selected ? selectedTextStyle : baseTextStyle),
        color: getColor(pd.sys.colour.content_primary),
      } as TextStyle,
    }));
  }, [data, selected, greenCheckIcon, baseTextStyle, selectedTextStyle]);

  return (
    <View style={styles.container}>
      <HeaderView
        startIcon={{
          icon: { icon: pd.icon.main.navigation.arrow_back_platform },
          onPress: handleClose,
        }}
        titleStart={currencyTitle || 'Currency'}
      />

      {showSearch && (
        <View style={styles.searchWrapper}>
          <TextFieldView
            value={searchValue}
            onChangeText={handleSearch}
            onEndIconPress={handleClearSearch}
            label={searchLabel}
            disabled={false}
          />
        </View>
      )}

      <FlatList
        ref={flatListRef}
        data={renderList}
        keyExtractor={(item) => item.id}
        showsVerticalScrollIndicator={false}
        renderItem={({ item }) => (
          <CurrencyRow
            primaryText={item.primaryText}
            primaryTextStyle={item.primaryTextStyle}
            isSelected={item.id === selected}
            onPress={() => handleSelect(item)}
          />
        )}
        ListEmptyComponent={() => (
          <Text
            style={[
              getTextStyle(
                pd.sys.typescale.body_text,
                pd.sys.colour.content_secondary,
              ),
              styles.emptyText,
            ]}
          >
            No results found
          </Text>
        )}
        contentContainerStyle={[
          styles.contentContainer,
          {
            paddingBottom: contentPaddingBottom,
            paddingHorizontal: contentPaddingHorizontal,
          },
        ]}
      />
    </View>
  );
};

// Factory form so Unistyles re-runs it on theme change; padding is applied
// inline at the call site since it comes from props, not the theme.
const styles = StyleSheet.create((pd) => ({
  container: {
    flex: 1,
    backgroundColor: getColor(pd.sys.colour.bg_primary),
  },
  searchWrapper: {
    paddingHorizontal: pd.ref.space.spacing40.px,
    marginBottom: pd.ref.space.spacing20.px,
  },
  emptyText: {
    textAlign: 'center',
    marginTop: pd.ref.space.spacing100.px,
  },
  contentContainer: {
    backgroundColor: getColor(pd.sys.colour.bg_primary),
    gap: pd.ref.space.spacing20.px,
  },
}));

export default CurrenciesView;
